'use client';

import { c as _c } from "react/compiler-runtime";
import { Liveline, type LivelinePoint, type LivelineSeries } from '@morfi/chart';
import { useReducedMotion } from 'motion/react';
import { useTheme } from 'next-themes';
import { useEffect, useRef, useState } from 'react';
import { resolveCssVar } from '@/components/chart/liveline-crosshair-style';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { cn } from '@/lib/ui/utils';

/** One point of a tier's own (un-summed) cumulative-earnings contribution. */
export interface EarningsLivelinePoint {
  readonly time: number; // unix seconds
  readonly value: number; // dollars (D6 ÷ 1e6)
}

/** A single stacked tier, bottom→top in array order. */
export interface EarningsLivelineSeries {
  readonly id: string;
  readonly label: string;
  /** A CSS colour token; resolved before it reaches the canvas boundary. */
  readonly color: string;
  /** This tier's own contribution per bucket (NOT the cumulative total). */
  readonly points: ReadonlyArray<EarningsLivelinePoint>;
  /** Latest contribution (live tip). */
  readonly value: number;
}
export interface EarningsSparklineLivelineProps {
  /** Tiers bottom→top. When `stacked`, the engine sums them; the top of the
   *  stack equals total earnings. One element renders a single filled area. */
  readonly series: ReadonlyArray<EarningsLivelineSeries>;
  readonly stacked: boolean;
  /** Currency formatter (D6 → display string) for the hover tooltip. */
  readonly formatValue: (value: number) => string;
  /** Formats a hovered bucket time (unix seconds) for the date pill. */
  readonly formatTooltipDate: (unixSeconds: number) => string;
  readonly className?: string;
}
const MIN_POINTS = 2;

// Endpoint dot + pulse sized down to match the portfolio value chart
// (winners-row preset) — the full-size default endpoint dominates a thin,
// flat line. Reserve just past `PULSE_SIZE` so the expanded ring clears the
// well edge; left stays minimal (the line starts there).
const PULSE_SIZE = 8;
const ENDPOINT_PAD: number = Math.ceil(PULSE_SIZE + 3);
const WELL_HEIGHT_CLASS = 'h-[80px]'; // ARBITRARY: 80px chart-well, matches the prior static sparkline

/**
 * Referrals earnings preview — canvas-based `<LiveChartSeries>` in true stacked-area
 * mode that replaces the static SVG stacked sparkline. Direct earnings form the
 * bottom band, Indirect stacks above; the top of the stack is total earnings.
 *
 * It is a **multi-series** chart, so the built-in series-toggle legend and
 * per-series endpoint labels/axes (which the chart shows by default for ≥ 2
 * series) are explicitly suppressed to keep the 80px well decorative. The
 * market-detail per-series-pill hover crosshair stays ON, showing each tier's
 * own contribution (the engine threads the un-summed value into the pill text).
 *
 * Off-screen rendering is gated by an `IntersectionObserver` (200 px prefetch)
 * with a sticky mount latch, so the canvas RAF loop never runs while scrolled
 * away.
 */
export function EarningsSparklineLiveline(t0) {
  const $ = _c(44);
  const {
    series,
    stacked,
    formatValue,
    formatTooltipDate,
    className
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const theme = resolvedTheme === "light" ? "light" : "dark";
  const reduceMotion = useReducedMotion() === true;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rootMargin: "200px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    isIntersecting
  } = useIntersectionObserver(containerRef, t1);
  const [mounted, setMounted] = useState(false);
  const [mountedAt] = useState(_temp);
  let t2;
  let t3;
  if ($[1] !== isIntersecting || $[2] !== mounted) {
    t2 = () => {
      if (isIntersecting && !mounted) {
        setMounted(true);
      }
    };
    t3 = [isIntersecting, mounted];
    $[1] = isIntersecting;
    $[2] = mounted;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== series[0]?.points) {
    t4 = series[0]?.points ?? [];
    $[5] = series[0]?.points;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const base = t4;
  const firstTime = base[0]?.time ?? 0;
  const lastTime = base[base.length - 1]?.time ?? firstTime;
  const windowSecs = Math.max(0, lastTime - firstTime);
  const timeOffset = mountedAt - lastTime;
  const enoughPoints = base.length >= MIN_POINTS;
  let t5;
  if ($[7] !== series || $[8] !== timeOffset) {
    let t6;
    if ($[10] !== timeOffset) {
      t6 = s => ({
        id: s.id,
        label: s.label,
        color: resolveCssVar(s.color),
        value: s.value,
        data: s.points.map(p => ({
          time: p.time + timeOffset,
          value: p.value
        }))
      });
      $[10] = timeOffset;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t5 = series.map(t6);
    $[7] = series;
    $[8] = timeOffset;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const chartSeries = t5;
  let t6;
  if ($[12] !== base || $[13] !== series || $[14] !== timeOffset) {
    let t7;
    if ($[16] !== series || $[17] !== timeOffset) {
      t7 = (point, pointIndex) => ({
        time: point.time + timeOffset,
        value: series.reduce((sum, entry) => sum + (entry.points[pointIndex]?.value ?? 0), 0)
      });
      $[16] = series;
      $[17] = timeOffset;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    t6 = base.map(t7);
    $[12] = base;
    $[13] = series;
    $[14] = timeOffset;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const totalPoints = t6;
  let t7;
  if ($[19] !== series) {
    t7 = series.reduce(_temp2, 0);
    $[19] = series;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const totalValue = t7;
  let t8;
  if ($[21] !== series) {
    t8 = resolveCssVar(series.at(-1)?.color ?? "var(--foreground-brand)");
    $[21] = series;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const totalColor = t8;
  let t9;
  if ($[23] !== reduceMotion) {
    t9 = reduceMotion ? {
      lerpSpeed: 1
    } : {};
    $[23] = reduceMotion;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const smoothingProp = t9;
  let t10;
  if ($[25] !== className) {
    t10 = cn("relative w-full", WELL_HEIGHT_CLASS, className);
    $[25] = className;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== chartSeries || $[28] !== enoughPoints || $[29] !== formatTooltipDate || $[30] !== formatValue || $[31] !== mounted || $[32] !== smoothingProp || $[33] !== stacked || $[34] !== theme || $[35] !== timeOffset || $[36] !== totalColor || $[37] !== totalPoints || $[38] !== totalValue || $[39] !== windowSecs) {
    t11 = mounted && enoughPoints ? <Liveline data={stacked ? totalPoints : []} value={stacked ? totalValue : 0} {...stacked ? {
      color: totalColor
    } : {
      series: chartSeries
    }} theme={theme} window={windowSecs} grid={false} scrub={true} badge={false} momentum={false} pulse={true} fill={stacked} formatValue={formatValue} formatTime={time => formatTooltipDate(time - timeOffset)} {...smoothingProp} padding={{
      top: ENDPOINT_PAD,
      right: ENDPOINT_PAD,
      bottom: ENDPOINT_PAD,
      left: 4
    }} className="block h-full w-full" /> : null;
    $[27] = chartSeries;
    $[28] = enoughPoints;
    $[29] = formatTooltipDate;
    $[30] = formatValue;
    $[31] = mounted;
    $[32] = smoothingProp;
    $[33] = stacked;
    $[34] = theme;
    $[35] = timeOffset;
    $[36] = totalColor;
    $[37] = totalPoints;
    $[38] = totalValue;
    $[39] = windowSecs;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== t10 || $[42] !== t11) {
    t12 = <div ref={containerRef} className={t10} data-testid="earnings-sparkline-liveline">{t11}</div>;
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  return t12;
}
function _temp2(sum_0, entry_0) {
  return sum_0 + entry_0.value;
}
function _temp() {
  return Math.floor(Date.now() / 1000);
}